import { HideObjectType } from '../../../../@types/enums';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import { SetTopFitView } from '../Three-JS-Viewer/Controllers/CameraControl';
import { addModulesToAllRoofsMaxFill, SetModuleGraph } from '../Three-JS-Viewer/Controllers/ModuleControl';
import { clearPendingUnghostAlert } from '../Three-JS-Viewer/Controllers/TreeImpactEligibility';

type Combo = { key: string; roofFaceIds: string[] };

type ReRunTreeScenariosArgs = {
    layoutKey?: string;
    onComplete?: () => void;
    selectedRoofCombinationKey: string;
    sceneViewer: any;
    reRunningTreeScenarios: boolean;
    defaultMaxfillRoofIds: string[];
    bestRoofsForLayout: { roofFaceIds?: string[] } | null | undefined;
    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>;
    simulateLayoutForRoofIds: (roofFaceIds: string[]) => Promise<any>;
    runGhostScenariosAndSave: () => Promise<void>;
    setTreeScenariosByLayoutKey: (next: (prev: Record<string, any[]>) => Record<string, any[]>) => void;
    setLayoutModulesByKey: (next: (prev: Record<string, number>) => Record<string, number>) => void;
    setLayoutKeyWhenTreeScenariosRun: (next: string) => void;
    setReRunningTreeScenarios: (next: boolean) => void;
    setIsDashboardOpen: (next: boolean) => void;
    setAiError: (next: string | null) => void;
    setTreeReprocessInProgress: (next: boolean) => void;
};

export function runReRunTreeScenariosAction(args: ReRunTreeScenariosArgs): void {
    const {
        layoutKey,
        onComplete,
        selectedRoofCombinationKey,
        sceneViewer,
        reRunningTreeScenarios,
        defaultMaxfillRoofIds,
        bestRoofsForLayout,
        roofCombinationsFromAi,
        simulateLayoutForRoofIds,
        runGhostScenariosAndSave,
        setTreeScenariosByLayoutKey,
        setLayoutModulesByKey,
        setLayoutKeyWhenTreeScenariosRun,
        setReRunningTreeScenarios,
        setIsDashboardOpen,
        setAiError,
        setTreeReprocessInProgress,
    } = args;

    const layoutAtRunStart = layoutKey ?? selectedRoofCombinationKey ?? 'default';
    if (!sceneViewer || reRunningTreeScenarios) return;
    if (useOneButtonStore.getState().dashboardContactsModalOpen) return;
    setTreeReprocessInProgress(true);
    clearPendingUnghostAlert();
    try {
        sceneViewer?.removeViewerAlert?.();
    } catch {
        // ignore
    }
    setIsDashboardOpen(false);
    setReRunningTreeScenarios(true);
    setAiError(null);
    useViewerStore.setState({ aiModuleFillingError: null } as any);
    const defaultIds = defaultMaxfillRoofIds.length > 0 ? defaultMaxfillRoofIds : (bestRoofsForLayout?.roofFaceIds ?? []);
    const combos: Combo[] = [
        { key: 'default', roofFaceIds: defaultIds },
        ...(roofCombinationsFromAi || []).map((c: any) => ({ key: c.name, roofFaceIds: c.roofFaceIds ?? [] })),
    ].filter((c) => (c.roofFaceIds?.length ?? 0) > 0);
    const combo = combos.find((c) => c.key === layoutAtRunStart) ?? combos[0];

    const run = async () => {
        try {
            const self = sceneViewer;
            try {
                self.setCameraView?.('3d');
                self.setVisibleGroup?.('maps', true);
                if ((self as any).mapCustom) (self as any).mapCustom.visible = true;
                self.setVisibleGroup?.('tree', true);
                self.hideObject?.(HideObjectType.GhostTree, false);
                SetTopFitView(self as any, {
                    includeRoofs: true,
                    includeTrees: true,
                    includeObstructions: true,
                    includeMapCustom: true,
                    padding: 1.25,
                    minZoom: 0.35,
                    maxZoom: 12,
                    height: 130,
                });
            } catch {
                // ignore camera/visibility issues
            }

            if (combo?.roofFaceIds?.length) {
                if (layoutAtRunStart === 'default') {
                    addModulesToAllRoofsMaxFill(self);
                    await SetModuleGraph(self);
                } else {
                    await simulateLayoutForRoofIds(combo.roofFaceIds);
                }
            }
            await runGhostScenariosAndSave();
            const err = (useViewerStore.getState() as any).aiModuleFillingError;
            if (err) setAiError(err);
            else {
                const scenarios = (useViewerStore.getState() as any).aiModuleFillingScenarios ?? [];
                if (scenarios.length > 0) {
                    setTreeScenariosByLayoutKey((prev) => {
                        const nextForKey = [...scenarios];
                        const merged = { ...prev, [layoutAtRunStart]: nextForKey };
                        try {
                            (useViewerStore as any).setState({
                                aiModuleFillingTreeScenariosByLayoutKey: merged,
                            });
                        } catch {
                            // ignore store sync errors
                        }
                        return merged;
                    });
                    const base = scenarios[0];
                    const baseModules = (base?.shadeWeights ?? []).reduce(
                        (sum: number, r: any) => sum + (r.moduleCount ?? 0),
                        0,
                    );
                    if (baseModules > 0) {
                        setLayoutModulesByKey((prev) =>
                            prev[layoutAtRunStart] !== undefined ? prev : { ...prev, [layoutAtRunStart]: baseModules },
                        );
                    }
                }
            }
        } catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            const errMsg = message || 'Tree-cutting reprocess failed.';
            try {
                setAiError(errMsg);
                useViewerStore.setState({ aiModuleFillingError: errMsg } as any);
            } catch {
                // ignore state update errors
            }
        } finally {
            setTreeReprocessInProgress(false);
            setLayoutKeyWhenTreeScenariosRun(layoutAtRunStart);
            setReRunningTreeScenarios(false);
            setIsDashboardOpen(true);
            onComplete?.();
        }
    };

    run().catch(() => {
        setTreeReprocessInProgress(false);
        setReRunningTreeScenarios(false);
        setIsDashboardOpen(true);
        onComplete?.();
    });
}
